import { useEffect, useState } from 'react';
import { SortableList } from '../components/SortableList';
import PageBaseLayout from '../layouts/PageBaseLayout';
import useBackdrop from '../hooks/useBackdrop';
import { updateBrandPriorities } from '../fetchFns';
import useSnackbar from '../hooks/useSnackbar';
import type { Merchant } from '../models/types';
import {
  Button,
  type AlertColor,
  type AlertPropsColorOverrides,
} from '@mui/material';
import type { OverridableStringUnion } from '@mui/types';
import { supabase } from '../supabase-client';

export function createRange<T>(
  length: number,
  initializer: (index: number) => T,
): T[] {
  return [...new Array(length)].map((_, index) => initializer(index));
}

function getMockItems() {
  return createRange(50, (index) => ({ id: index + 1 }));
}

export default function BrandsPriority() {
  // const [items, setItems] = useState(getMockItems);
  const [items, setItems] = useState<Merchant[]>([]);
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();

  const handleChangeItems = (newItems: Merchant[]) => {
    const merchantsWithNewPriorities = newItems.map((merchant, index) => ({
      ...merchant,
      priority: index + 1,
    }));
    setItems(merchantsWithNewPriorities);
  };

  const getData = async () => {
    backdrop.handleBackdropOpen();

    try {
      const { data, error } = await supabase
        .schema('admin_backend')
        .from('merchants_admin_view')
        .select('id, name, priority');

      if (error) {
        console.error('Error fetching brands:', error);
        throw error;
      }

      const mappedBrands = (data as any[]).sort(
        (a, b) => a.priority - b.priority,
      );

      setItems(mappedBrands);
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast.stack);
      snackbar.handleSnackbarOpen(errorCast.message, 'error');
    }

    backdrop.handleBackdropClose();
  };

  const handleSaveBrandOrder = async () => {
    backdrop.handleBackdropOpen();
    let message = 'Brand order saved successfully.';
    let severity:
      | OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
      | undefined = 'success';

    try {
      const response = await updateBrandPriorities(items);
      console.log(items);
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast.stack);
      message = errorCast.message;
      severity = 'error';
    }
    snackbar.handleSnackbarOpen(message, severity);
    backdrop.handleBackdropClose();
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <PageBaseLayout title="Brands Priority">
      <Button variant="contained" onClick={handleSaveBrandOrder}>
        Save Brand Order
      </Button>
      <SortableList
        items={items}
        onChange={handleChangeItems}
        renderItem={(item) => (
          <SortableList.Item id={item.id}>
            <SortableList.DragHandle />
            Brand: {item.name}
          </SortableList.Item>
        )}
      />
    </PageBaseLayout>
  );
}
